import { CELLS, CELL_PX, GRID, TILE_PX, decodeTile, encodeTile, type DecodedMark, type MarkVersion } from './screenWatermark.ts';

/**
 * Recovering the screen mark from a picture of a screen.
 *
 * The mark is around a hundred times fainter than the page over it, so nothing here thresholds
 * pixels: one line of body text outweighs the entire payload. Two properties carry the recovery.
 *
 * It is local. Each dot cell is scored as its middle against the ring of pixels immediately around it,
 * so a gradient, a card edge or a block of colour moves a cell and its surround together and falls
 * out of the difference. The accessible horizontal mark uses the line's crest against the gaps
 * above and below, with an additional pass that excludes text by its local variance. Both feed
 * the same codeword validation and independent-tile evidence rules.
 *
 * It repeats. Every tile holds the same sixty-four cells, so the response map is folded modulo the
 * tile and the tiles stack. Page content does not repeat on a sixty-four pixel pitch, so it lands in
 * different cells each time and cancels; the mark lands in the same cell every time and builds. The
 * stack is combined with a median rather than a mean, so the few tiles that fell across a heading
 * are outvoted instead of dragging their cells with them, and samples that could only be paint or an
 * edge — a response many times larger than the mark can produce — are left out before the median is
 * taken, so a scribble over the screenshot costs the tiles it covers and nothing else.
 *
 * Alignment is then a small search — the crop can start on any pixel — and the head cells and the
 * code say which offset was right. Folding first is what makes that search cheap: it collapses the
 * whole image to one tile before anything is tried, so the search costs thousands of lookups rather
 * than billions of pixel reads.
 *
 * What comes back is graded. A zero-error v2 decode satisfies forty check bits, so a clean decode
 * qualifies as a read under the current rules; what
 * the rest of the image says about it — whether disjoint groups of tiles read the same word, how
 * many tiles decode to the same digest on their own — is reported as the grade and its evidence, so
 * a person can tell a read that stands on its own from one that stands on corroboration. A decode
 * that needed cells put back and found no support is not a read: it rides on the failure as a
 * candidate, for someone to check against a second sample, and never as an answer.
 */

export interface ReadOptions {
  /** Pixel scales to try. A retina screenshot is 2; a crop pasted at native size is 1. */
  scales?: number[];
}

/**
 * How firmly a read stands. `confirmed` and `likely` are reads; `unconfirmed` is a candidate that
 * nothing corroborated and is never presented as an answer.
 */
export type Grade = 'confirmed' | 'likely' | 'unconfirmed';

export interface ReadResult extends DecodedMark {
  /** Pixel scale the tile was found at: 2 means a retina screenshot. */
  scale: number;
  /** Where the tile grid starts in the image, in image pixels. */
  origin: { x: number; y: number };
  /** Whole tiles inside the image at that origin. Edge-inclusive line splits can use partial tiles too. */
  tiles: number;
  /**
   * Gap between the cells read as one and the cells read as zero, over the spread inside each
   * group. Above about 2 the read is clean; near 1 the code is doing most of the work.
   */
  confidence: number;
  grade: Grade;
  /**
   * What lifted the grade: the split whose groups agreed ('tile parity'), the tiles that voted
   * ('tiles 5 of 9'), 'separation alone' for a clipping too small to split, or 'none'.
   */
  corroboration: string;
  /**
   * How the whole tiles voted when each was decoded on its own. Tiles that read the same sixty-four
   * cells as another are counted once, so `for + against + abstained` can fall short of `of`.
   */
  votes: { for: number; against: number; abstained: number; of: number };
  /** The tiles that voted for this digest, with the cells each had put back. */
  restitch?: { corrected: number; tiles: { tx: number; ty: number; errors: number }[] };
}

export interface ReadFailure {
  ok: false;
  reason: string;
  /** What was attempted, so "too small a clipping" can be told from "no mark here". */
  tried: { scale: number; tiles: number }[];
  /**
   * A digest that decoded but that nothing corroborated. Not a read: it is what luck in the noise
   * looks like as well as what a damaged mark looks like, so it is never matched to a name on its
   * own. It is kept so a person can be told what to look for in a second sample.
   */
  candidate?: ReadResult;
}

export type ReadOutcome = ({ ok: true } & ReadResult) | ReadFailure;

export interface RasterImage {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

// ── Budgets ─────────────────────────────────────────────────────────────────
//
// Decoder correction and evidence budgets retain the original measured boundaries
// (docs/SCREEN_MARK_V2.md). The horizontal filters add optical masks, not looser decoding budgets.

/** A sample whose response magnitude is beyond this is paint or an edge, not the mark, which produces about 4.4. */
const MASK_LIMIT = 30;
/** Text can cancel in the response while still varying strongly inside the window. */
const LINE_CONTENT_VARIANCE = 16;
/** Near-zero line response supplies no carrier evidence: it can be uniform paint, a wrong phase or roundoff. */
const LINE_RESPONSE_FLOOR = 1e-6;
/** Wrong cells the folded tile may carry and still be a candidate. */
const FOLD_ERRORS = 2;
/** Wrong cells a single tile may carry and still cast a vote. */
const TILE_ERRORS = 3;
/** Distinct tiles that have to vote for a digest before the votes confirm it — and the number a rival digest must stay under. */
const VOTES_TO_CONFIRM = 2;
/** Distinct tiles that have to vote for a candidate that needed cells put back before it is even likely. */
const VOTES_TO_PROMOTE = 3;
/** Fewer whole tiles than this is a clipping too small to split into groups. */
const SMALL_CROP_TILES = 4;
/** What a clipping too small to split has to reach on separation instead. */
const SMALL_CROP_SEPARATION = 8;
/** Origins the vote-only fallback tries, taken by fold separation. */
const FALLBACK_ORIGINS = 8;
/** Candidates graded per scale, best first, before the rest are let go. */
const GRADED_CANDIDATES = 8;
/** Where separation stops being reported: past this the one and zero responses are fully separated. */
const SEPARATION_CAP = 999;

// ── The response map and its fold ───────────────────────────────────────────

/** Rec. 601 luma. The mark is drawn in neutral ink or white, so brightness alone carries it. */
function luminance({ data, width, height }: RasterImage): Float32Array {
  const out = new Float32Array(width * height);
  for (let index = 0, pixel = 0; pixel < out.length; index += 4, pixel += 1) {
    out[pixel] = data[index] * 0.299 + data[index + 1] * 0.587 + data[index + 2] * 0.114;
  }
  return out;
}

/** Summed-area table, so every box total below is four lookups regardless of the box's size. */
function integral(luma: Float32Array, width: number, height: number, squared = false): Float64Array {
  const stride = width + 1;
  const table = new Float64Array(stride * (height + 1));
  for (let y = 0; y < height; y += 1) {
    let row = 0;
    for (let x = 0; x < width; x += 1) {
      const value = luma[y * width + x];
      row += squared ? value * value : value;
      table[(y + 1) * stride + x + 1] = table[y * stride + x + 1] + row;
    }
  }
  return table;
}

const boxSum = (table: Float64Array, stride: number, x0: number, y0: number, x1: number, y1: number): number =>
  table[y1 * stride + x1] - table[y0 * stride + x1] - table[y1 * stride + x0] + table[y0 * stride + x0];

/**
 * How much a cell-sized window stands out from itself: its middle against its own ring.
 *
 * Unsigned here. The mark is white on the dark theme and ink on the light one, so the sign is
 * decided later by trying both and seeing which one decodes.
 */
function responseMap(
  luma: Float32Array, width: number, height: number, cell: number, horizontal = false, maskContent = false,
): { map: Float32Array; mapWidth: number; mapHeight: number } {
  const table = integral(luma, width, height);
  const squares = maskContent ? integral(luma, width, height, true) : null;
  const stride = width + 1;
  const inner = Math.max(1, Math.round(cell * 0.375));
  const pad = Math.floor((cell - inner) / 2);
  const mapWidth = Math.max(0, width - cell + 1);
  const mapHeight = Math.max(0, height - cell + 1);
  const map = new Float32Array(mapWidth * mapHeight);
  const innerArea = inner * inner;
  const ringArea = cell * cell - innerArea;

  // A horizontal mark carries the same bits as dots, with brightness changing smoothly along
  // each line. Compare its centre with the quiet gaps immediately above and below, instead of
  // subtracting the line on either side as the dot filter does. Keeping the window narrow along
  // the line avoids mixing adjacent bits. The line filters reject constant colour and vertical
  // linear ramps. Their outputs have the same luminance units as the dot response.
  const band = Math.max(1, Math.round(cell * 0.25));
  const bandPad = Math.floor((cell - band) / 2);
  const edge = Math.max(1, Math.round(cell * 0.125));
  const weights = Float64Array.from({ length: cell }, (_, row) => -Math.cos(2 * Math.PI * (row + 0.5) / cell));
  const weightSum = weights.reduce((sum, weight) => sum + Math.max(0, weight), 0);

  for (let y = 0; y < mapHeight; y += 1) {
    for (let x = 0; x < mapWidth; x += 1) {
      if (horizontal) {
        if (squares) {
          // Text can have a small net response when its edges cancel, yet swamp the faint carrier.
          // This pass excludes such windows before folding and uses every row of the sinusoid.
          // The unmasked peak/gap pass remains available for gradients and noisy screenshots.
          const area = inner * cell;
          const mean = boxSum(table, stride, x + pad, y, x + pad + inner, y + cell) / area;
          const variance = boxSum(squares, stride, x + pad, y, x + pad + inner, y + cell) / area - mean * mean;
          if (variance > LINE_CONTENT_VARIANCE) { map[y * mapWidth + x] = MASK_LIMIT + 1; continue; }
          let value = 0;
          for (let row = 0; row < cell; row += 1) {
            value += weights[row] * boxSum(table, stride, x + pad, y + row, x + pad + inner, y + row + 1);
          }
          const score = value / (inner * weightSum);
          map[y * mapWidth + x] = Math.abs(score) < LINE_RESPONSE_FLOOR ? MASK_LIMIT + 1 : score;
          continue;
        }
        const middle = boxSum(table, stride, x + pad, y + bandPad, x + pad + inner, y + bandPad + band);
        const gaps = boxSum(table, stride, x + pad, y, x + pad + inner, y + edge)
          + boxSum(table, stride, x + pad, y + cell - edge, x + pad + inner, y + cell);
        const score = middle / (inner * band) - gaps / (inner * edge * 2);
        map[y * mapWidth + x] = Math.abs(score) < LINE_RESPONSE_FLOOR ? MASK_LIMIT + 1 : score;
        continue;
      }
      const whole = boxSum(table, stride, x, y, x + cell, y + cell);
      const middle = boxSum(table, stride, x + pad, y + pad, x + pad + inner, y + pad + inner);
      map[y * mapWidth + x] = middle / innerArea - (whole - middle) / ringArea;
    }
  }
  return { map, mapWidth, mapHeight };
}

/** A sample the mark could have produced. Anything larger is page content or paint and is left out. */
const unmasked = (value: number): boolean => value <= MASK_LIMIT && value >= -MASK_LIMIT;

/** Median of a run of values that has already been sorted. */
const sortedMedian = (values: Float32Array): number => {
  const middle = values.length >> 1;
  return values.length % 2 ? values[middle] : (values[middle - 1] + values[middle]) / 2;
};

/**
 * Collapses the response map onto a single tile, taking the median of everything that landed on
 * each position. This is where the page stops mattering and the mark starts.
 *
 * Samples beyond the mask limit are dropped before the median rather than voted down by it. A
 * white brush stroke makes responses ten times what the mark can, and its edges would otherwise
 * set the extremes that the threshold is taken between; with them gone the fold holds the clean
 * signal until the paint outnumbers the page. A position every sample was dropped from reads as
 * zero, which is what a blank cell reads as anyway.
 */
function fold(map: Float32Array, mapWidth: number, mapHeight: number, tile: number): Float32Array {
  const buckets = tile * tile;
  const counts = new Int32Array(buckets);
  for (let y = 0; y < mapHeight; y += 1) {
    const row = (y % tile) * tile;
    for (let x = 0, column = 0; x < mapWidth; x += 1, column = column + 1 === tile ? 0 : column + 1) {
      if (unmasked(map[y * mapWidth + x])) counts[row + column] += 1;
    }
  }
  const starts = new Int32Array(buckets + 1);
  for (let index = 0; index < buckets; index += 1) starts[index + 1] = starts[index] + counts[index];
  const cursor = starts.slice(0, buckets);
  const values = new Float32Array(starts[buckets]);
  for (let y = 0; y < mapHeight; y += 1) {
    const row = (y % tile) * tile;
    for (let x = 0, column = 0; x < mapWidth; x += 1, column = column + 1 === tile ? 0 : column + 1) {
      const value = map[y * mapWidth + x];
      if (unmasked(value)) values[cursor[row + column]++] = value;
    }
  }
  const folded = new Float32Array(buckets);
  for (let index = 0; index < buckets; index += 1) {
    if (!counts[index]) continue;
    const run = values.subarray(starts[index], starts[index + 1]);
    run.sort();
    folded[index] = sortedMedian(run);
  }
  return folded;
}

/**
 * Gap between the cells read as dots and the cells read as blank, over the spread within each
 * group. Label-free: it is taken from whichever word the threshold produced, so it says how cleanly
 * an origin splits into two levels whether or not those levels decode to anything.
 */
function separation(raw: number[], cells: boolean[]): number {
  let dotSum = 0, dotCount = 0, blankSum = 0, blankCount = 0;
  for (let index = 0; index < CELLS; index += 1) {
    if (cells[index]) { dotSum += raw[index]; dotCount += 1; } else { blankSum += raw[index]; blankCount += 1; }
  }
  const dotMean = dotSum / Math.max(dotCount, 1);
  const blankMean = blankSum / Math.max(blankCount, 1);
  let dotVariance = 0, blankVariance = 0;
  for (let index = 0; index < CELLS; index += 1) {
    if (cells[index]) dotVariance += (raw[index] - dotMean) ** 2;
    else blankVariance += (raw[index] - blankMean) ** 2;
  }
  const gap = Math.abs(dotMean - blankMean);
  const dotSpread = dotCount > 1 ? Math.sqrt(dotVariance / dotCount) : 0;
  const blankSpread = blankCount > 1 ? Math.sqrt(blankVariance / blankCount) : 0;
  // Capped: a lossless crop can have groups so tight that the ratio runs to seven figures, which
  // reads as a bug rather than a firmer answer. Anything at the cap is already past doubt, and the
  // cap is exact so that origins which all reach it rank as the ties they are.
  return Math.min(gap / Math.max((dotSpread + blankSpread) / 2, 1e-6), SEPARATION_CAP);
}

/** One pixel scale of one image: the response map and its fold, which every route reads from. */
interface Field {
  map: Float32Array;
  mapWidth: number;
  mapHeight: number;
  width: number;
  height: number;
  scale: number;
  cell: number;
  tile: number;
  folded: Float32Array;
  horizontal: boolean;
}

function prepare(luma: Float32Array, width: number, height: number, scale: number, horizontal = false, maskContent = false): Field | null {
  const cell = CELL_PX * scale;
  const tile = TILE_PX * scale;
  const { map, mapWidth, mapHeight } = responseMap(luma, width, height, cell, horizontal, maskContent);
  if (mapWidth < tile || mapHeight < tile) return null;
  return { map, mapWidth, mapHeight, width, height, scale, cell, tile, folded: fold(map, mapWidth, mapHeight, tile), horizontal };
}

/** The folded score of each cell when the tile grid starts at an origin. */
function scoresAt(field: Field, originX: number, originY: number): number[] {
  const { folded, tile, cell } = field;
  const raw = new Array<number>(CELLS);
  for (let index = 0; index < CELLS; index += 1) {
    const x = (originX + (index % GRID) * cell) % tile;
    const y = (originY + Math.floor(index / GRID) * cell) % tile;
    raw[index] = folded[y * tile + x];
  }
  return raw;
}

/**
 * Lines have a continuous carrier even through zero bits. Dots do not. Require that carrier
 * before trying the line filter, so an additional filter cannot reinterpret damaged dots as a
 * more certain read. Compare decoded zero and one cells separately: a payload may have far more
 * ones than zeros, so quartiles of all cells would mistake dense dots for continuous lines.
 */
function hasHorizontalCarrier(raw: number[], sign: number, pattern: boolean[]): boolean {
  const zeros = Float32Array.from(raw.filter((_value, index) => !pattern[index]), (value) => value * sign).sort();
  const ones = Float32Array.from(raw.filter((_value, index) => pattern[index]), (value) => value * sign).sort();
  if (!zeros.length || !ones.length) return false;
  const zero = sortedMedian(zeros), one = sortedMedian(ones);
  // Cosine weights can leave floating-point cancellation residue on constant pixels. It is not
  // an optical carrier: reject it before the code can accidentally read those numerical crumbs.
  return one > zero && zero > Math.max(LINE_RESPONSE_FLOOR, one * 0.2);
}

// ── Reading the tiles one at a time ─────────────────────────────────────────

/**
 * The single-sample response of every cell of every whole tile at an origin, tile by tile.
 *
 * A tile is whole when all sixty-four of its cells sit inside the image; the crop's phase decides
 * how many there are. Nothing is masked here — the routes that read these decide what a dropped
 * sample means to them.
 */
function tileSamples(field: Field, originX: number, originY: number): { cols: number; rows: number; samples: Float32Array } {
  const { map, mapWidth, width, height, cell, tile } = field;
  const cols = Math.max(0, Math.floor((width - originX) / tile));
  const rows = Math.max(0, Math.floor((height - originY) / tile));
  const samples = new Float32Array(cols * rows * CELLS);
  for (let ty = 0; ty < rows; ty += 1) {
    const top = originY + ty * tile;
    for (let tx = 0; tx < cols; tx += 1) {
      const left = originX + tx * tile;
      const base = (ty * cols + tx) * CELLS;
      for (let index = 0; index < CELLS; index += 1) {
        samples[base + index] = map[(top + Math.floor(index / GRID) * cell) * mapWidth + left + (index % GRID) * cell];
      }
    }
  }
  return { cols, rows, samples };
}

/**
 * One tile's cells, read with the fold's threshold and polarity. A sample that was masked out of
 * the fold reads as blank here: a cell under paint is not a dot, whatever the paint did to it.
 */
function tileCells(samples: Float32Array, tileIndex: number, sign: number, threshold: number): boolean[] {
  const base = tileIndex * CELLS;
  const cells = new Array<boolean>(CELLS);
  for (let index = 0; index < CELLS; index += 1) {
    const value = samples[base + index];
    cells[index] = unmasked(value) && (sign > 0 ? value > threshold : value < threshold);
  }
  return cells;
}

interface Ballot {
  version: MarkVersion;
  digest: string;
  count: number;
  voters: { tx: number; ty: number; errors: number }[];
}

/**
 * Decodes every whole tile on its own and tallies what they say, per digest.
 *
 * A tile that does not decode abstains. Two tiles that read the same sixty-four cells are counted
 * once when `distinct` is set: a page element repeating on the tile pitch puts the same word in
 * every tile, and that is one piece of evidence however many times it appears, not several. Tiles
 * that reached the same digest through different damage are different pieces of evidence.
 */
function tallyTiles(
  samples: Float32Array, cols: number, rows: number, sign: number, threshold: number, maxErrors: number, distinct: boolean,
): { ballots: Ballot[]; abstained: number } {
  const ballots = new Map<string, Ballot>();
  const seen = new Set<string>();
  let abstained = 0;
  for (let tileIndex = 0; tileIndex < cols * rows; tileIndex += 1) {
    const cells = tileCells(samples, tileIndex, sign, threshold);
    const mark = decodeTile(cells, maxErrors);
    if (!mark) { abstained += 1; continue; }
    if (distinct) {
      const word = cells.map((cell) => (cell ? '1' : '0')).join('');
      if (seen.has(word)) continue;
      seen.add(word);
    }
    const key = `${mark.version}:${mark.digest}`;
    const ballot = ballots.get(key) ?? { version: mark.version, digest: mark.digest, count: 0, voters: [] };
    ballot.count += 1;
    ballot.voters.push({ tx: tileIndex % cols, ty: Math.floor(tileIndex / cols), errors: mark.errors });
    ballots.set(key, ballot);
  }
  return { ballots: [...ballots.values()].sort((left, right) => right.count - left.count), abstained };
}

// ── Corroborating a candidate ───────────────────────────────────────────────

/**
 * Ways of splitting the whole tiles into groups that share no tile.
 *
 * A brush stroke across the top of an image takes every other tile from both parity groups but
 * leaves the left and right halves whole; a stroke down the middle does the reverse. Thirds are for
 * the case where one region is ruined and the rest is fine: any two of the three agreeing is still
 * two independent readings of the same word.
 */
const SPLITS: { name: string; groups: number; group: (tx: number, ty: number, cols: number, rows: number) => number }[] = [
  { name: 'tile parity', groups: 2, group: (tx, ty) => (tx + ty) % 2 },
  { name: 'left and right halves', groups: 2, group: (tx, _ty, cols) => (tx < cols / 2 ? 0 : 1) },
  { name: 'top and bottom halves', groups: 2, group: (_tx, ty, _cols, rows) => (ty < rows / 2 ? 0 : 1) },
  { name: 'thirds', groups: 3, group: (tx, ty) => (tx + ty) % 3 },
];

/** Fixed disjoint partitions for lines obscured by rows of text. Never chosen from the candidate. */
const LINE_SPLITS: typeof SPLITS = [
  { name: 'row parity', groups: 2, group: (_tx, ty) => ty % 2 },
  { name: 'column parity', groups: 2, group: (tx) => tx % 2 },
  { name: 'block parity', groups: 2, group: (tx, ty) => (Math.floor(tx / 2) + Math.floor(ty / 2)) % 2 },
];

/**
 * Whether one group of tiles, read on its own, gives exactly the candidate's word.
 *
 * The group is not asked to find its own threshold. With a third of the tiles, one cell that landed
 * on a heading has a median far outside the rest, and a midpoint of the extremes then puts half the
 * cells on the wrong side of a pattern the group otherwise agrees with. Instead the group's cells
 * are ranked and the candidate's dot count taken from the top, which no single outlier can move.
 * Scores are signed by the fold's polarity so that dots rank high on both themes.
 */
function groupAgrees(samples: Float32Array, members: number[], sign: number, pattern: boolean[], dots: number, requireCoverage = false): boolean {
  if (!members.length || dots <= 0 || dots >= CELLS) return false;
  const scores = new Array<number>(CELLS);
  const values = new Float32Array(members.length);
  for (let index = 0; index < CELLS; index += 1) {
    let count = 0;
    for (const tileIndex of members) {
      const value = samples[tileIndex * CELLS + index];
      if (unmasked(value)) values[count++] = value;
    }
    if (!count) {
      if (requireCoverage) return false;
      scores[index] = 0;
      continue;
    }
    const run = values.subarray(0, count);
    run.sort();
    scores[index] = sign * sortedMedian(run);
  }
  const ranked = [...scores].sort((left, right) => right - left);
  const threshold = (ranked[dots - 1] + ranked[dots]) / 2;
  for (let index = 0; index < CELLS; index += 1) {
    if ((scores[index] > threshold) !== pattern[index]) return false;
  }
  return true;
}

/** The first split in which two groups, sharing no tile, each read the candidate's word on their own. */
function corroboratingSplit(samples: Float32Array, cols: number, rows: number, sign: number, pattern: boolean[], horizontal = false): string | null {
  const dots = pattern.filter(Boolean).length;
  for (const split of horizontal ? [...SPLITS, ...LINE_SPLITS] : SPLITS) {
    const members: number[][] = Array.from({ length: split.groups }, () => []);
    for (let ty = 0; ty < rows; ty += 1) {
      for (let tx = 0; tx < cols; tx += 1) members[split.group(tx, ty, cols, rows)].push(ty * cols + tx);
    }
    let agreeing = 0;
    for (const group of members) {
      if (groupAgrees(samples, group, sign, pattern, dots, horizontal)) agreeing += 1;
      if (agreeing >= 2) return split.name;
    }
  }
  return null;
}

/**
 * Boundary tiles still contain useful cells. For lines, also split all fully visible cell
 * windows by their tile membership, including incomplete tiles at the crop's edges. No window
 * belongs to two groups. Missing or masked cells are unknown, never blank: each corroborating
 * group must contain an actual unmasked observation of every bit and match all 64 exactly.
 */
function partialBoundarySplit(field: Field, originX: number, originY: number, sign: number, pattern: boolean[]): string | null {
  const { tile, cell, mapWidth, mapHeight, map } = field;
  const minX = Math.ceil((-originX - (GRID - 1) * cell) / tile);
  const minY = Math.ceil((-originY - (GRID - 1) * cell) / tile);
  const maxX = Math.floor((mapWidth - 1 - originX) / tile);
  const maxY = Math.floor((mapHeight - 1 - originY) / tile);
  const cols = maxX - minX + 1, rows = maxY - minY + 1;
  const samples = new Float32Array(cols * rows * CELLS).fill(NaN);
  for (let ty = 0; ty < rows; ty += 1) for (let tx = 0; tx < cols; tx += 1) {
    const left = originX + (minX + tx) * tile, top = originY + (minY + ty) * tile;
    for (let index = 0; index < CELLS; index += 1) {
      const x = left + (index % GRID) * cell, y = top + Math.floor(index / GRID) * cell;
      if (x >= 0 && x < mapWidth && y >= 0 && y < mapHeight) samples[(ty * cols + tx) * CELLS + index] = map[y * mapWidth + x];
    }
  }
  const dots = pattern.filter(Boolean).length;
  for (const split of [...SPLITS, ...LINE_SPLITS]) {
    const groups: number[][] = Array.from({ length: split.groups }, () => []);
    for (let ty = 0; ty < rows; ty += 1) for (let tx = 0; tx < cols; tx += 1) groups[split.group(tx, ty, cols, rows)].push(ty * cols + tx);
    if (groups.filter((members) => groupAgrees(samples, members, sign, pattern, dots, true)).length >= 2) return `edge-inclusive ${split.name}`;
  }
  return null;
}

/**
 * Where a candidate starts before anything corroborates it.
 *
 * A v2 candidate with nothing put back has satisfied forty exact bits at one alignment, which noise
 * does about once in a trillion trials: that is a read on its own. One that needed cells put back
 * has satisfied fewer, and a v1 tile only ever had twenty-four to satisfy — the deep read's trials
 * clear that by luck once in a few dozen images, which is what happened to the first unmarked image
 * the tool was pointed at. So neither is a read until something else in the image agrees.
 */
const baseGrade = (mark: DecodedMark): Grade => (mark.version === 2 && mark.errors === 0 ? 'likely' : 'unconfirmed');

/**
 * Grades the candidate the fold produced by reading the tiles again, other ways.
 *
 * Three routes confirm a clean decode, any one being enough: two disjoint groups of tiles in some
 * split each read the same word on their own; enough tiles decode to the same digest on their own
 * with no other digest keeping up; or, for a clipping too small to split, the separation is beyond
 * what noise reaches. A candidate that needed cells put back cannot be confirmed by anything, but
 * enough tiles agreeing on their own makes it likely — the tampered-image route, where the tiles
 * between the strokes are clean even though the fold across them was not.
 */
function grade(field: Field, originX: number, originY: number, sign: number, threshold: number, mark: DecodedMark, confidence: number): ReadResult {
  const { cols, rows, samples } = tileSamples(field, originX, originY);
  const tiles = cols * rows;
  const { ballots, abstained } = tallyTiles(samples, cols, rows, sign, threshold, TILE_ERRORS, true);
  const own = ballots.find((ballot) => ballot.version === mark.version && ballot.digest === mark.digest);
  const support = own?.count ?? 0;
  const rival = Math.max(0, ...ballots.filter((ballot) => ballot !== own).map((ballot) => ballot.count));
  const votes = { for: support, against: ballots.reduce((total, ballot) => total + ballot.count, 0) - support, abstained, of: tiles };
  const unrivalled = rival < VOTES_TO_CONFIRM;

  let level = baseGrade(mark);
  let corroboration = 'none';
  if (mark.errors === 0) {
    const pattern = encodeTile(mark.digest, mark.version);
    const split = corroboratingSplit(samples, cols, rows, sign, pattern, field.horizontal)
      ?? (field.horizontal ? partialBoundarySplit(field, originX, originY, sign, pattern) : null);
    if (split) { level = 'confirmed'; corroboration = split; }
    else if (support >= VOTES_TO_CONFIRM && unrivalled) { level = 'confirmed'; corroboration = `tiles ${support} of ${tiles}`; }
    else if (tiles < SMALL_CROP_TILES && confidence >= SMALL_CROP_SEPARATION) { level = 'confirmed'; corroboration = 'separation alone'; }
  }
  if (level === 'unconfirmed' && support >= VOTES_TO_PROMOTE && unrivalled) {
    level = 'likely';
    corroboration = `tiles ${support} of ${tiles}`;
  }

  return {
    ...mark,
    scale: field.scale,
    origin: { x: originX, y: originY },
    tiles,
    confidence,
    grade: level,
    corroboration,
    votes,
    ...(own ? { restitch: { corrected: own.voters.reduce((total, voter) => total + voter.errors, 0), tiles: own.voters } } : {}),
  };
}

// ── The search ──────────────────────────────────────────────────────────────

interface Origin { x: number; y: number; threshold: number; separation: number }

/**
 * How many bits of screen a decode cleared, which is what its credibility rests on.
 *
 * A v2 word with nothing put back satisfied forty exact bits; one with a cell put back could have
 * been any of sixty-five words, and one with two any of over two thousand, which is six and eleven
 * bits fewer. A v1 word only ever had twenty-four to satisfy. So a v2 decode that needed two cells
 * put back is still thirty times harder for noise to produce than a v1 decode that needed none,
 * and the order candidates are kept in says so.
 */
const screenBits = (mark: { version: number; errors: number }): number =>
  (mark.version === 2 ? [40, 34, 29][mark.errors] ?? 29 : 24);

interface Evidence { version: number; errors: number; confidence: number }

/** The order candidates are preferred in: the stronger screen, then fewer cells put back, then the wider separation. */
const byEvidence = (left: Evidence, right: Evidence): number =>
  screenBits(right) - screenBits(left) || left.errors - right.errors || right.confidence - left.confidence;

const outranks = (candidate: Evidence, incumbent: Evidence | null): boolean => incumbent === null || byEvidence(candidate, incumbent) < 0;

/**
 * Reads the tiles alone at the origins where the fold separates most cleanly, for when the fold
 * itself would not decode.
 *
 * This is the route for a fold poisoned by paint: on a large screenshot most tiles are under a
 * stroke somewhere, and their medians carry a few wrong cells even after masking, while the tiles
 * between the strokes are untouched. Each tile has to decode exactly — forty bits, no cells put
 * back — and two of them reading the same digest is likely, three is confirmed. The origins are
 * chosen by how well the fold's own thresholded word separates, which needs no decode to compute.
 */
function readTilesAlone(field: Field, sign: number, origins: Origin[]): ReadResult | null {
  let best: ReadResult | null = null;
  for (const origin of origins) {
    const { cols, rows, samples } = tileSamples(field, origin.x, origin.y);
    const { ballots, abstained } = tallyTiles(samples, cols, rows, sign, origin.threshold, 0, false);
    const [leader, runnerUp] = ballots;
    if (!leader || leader.count < VOTES_TO_CONFIRM || (runnerUp && runnerUp.count >= VOTES_TO_CONFIRM)) continue;
    if (field.horizontal && (leader.version !== 2 || !hasHorizontalCarrier(scoresAt(field, origin.x, origin.y), sign, encodeTile(leader.digest, 2)))) continue;
    const tiles = cols * rows;
    const read: ReadResult = {
      version: leader.version,
      digest: leader.digest,
      errors: 0,
      units: { head: 0, a: 0, b: 0 },
      scale: field.scale,
      origin: { x: origin.x, y: origin.y },
      tiles,
      confidence: origin.separation,
      grade: leader.count >= VOTES_TO_PROMOTE ? 'confirmed' : 'likely',
      corroboration: `exact tiles ${leader.count} of ${tiles}`,
      votes: { for: leader.count, against: ballots.reduce((total, ballot) => total + ballot.count, 0) - leader.count, abstained, of: tiles },
      restitch: { corrected: 0, tiles: leader.voters },
    };
    if (best === null || read.votes.for > best.votes.for || (read.votes.for === best.votes.for && read.confidence > best.confidence)) best = read;
  }
  return best;
}

interface Candidate { x: number; y: number; sign: number; threshold: number; mark: DecodedMark; version: number; errors: number; confidence: number; tiles: number }

/**
 * The evidence order, then — only when two origins tie exactly, which happens at the separation
 * cap on a lossless clip — the alignment with more whole tiles, since it covers the most of the image.
 */
const byRank = (left: Candidate, right: Candidate): number => byEvidence(left, right) || right.tiles - left.tiles;

/**
 * Searches one pixel scale: folds the image onto a tile, then tries every offset within it.
 *
 * Both polarities are tried because the mark is white on the dark theme and ink on the light one.
 * Every origin is decoded with a small budget for wrong cells, and the best candidates — the
 * strongest screen, then fewest cells put back, then the widest separation — are graded in that
 * order until one is confirmed. More than one is graded because the origins a pixel either side
 * of the true one decode just as well and separate about as well, and the one that separates
 * widest is not always the one whose tiles agree exactly when read apart. Where a polarity
 * produced no candidate at all, the tiles are read alone at the origins that separate best, in
 * case the fold was what failed rather than the mark.
 */
function searchScale(field: Field): { read: ReadResult | null; candidate: ReadResult | null } {
  const { tile } = field;
  const shortlist: Candidate[] = [];
  const decoded = { [1]: false, [-1]: false };
  const cleanest: Origin[] = [];

  for (let originY = 0; originY < tile; originY += 1) {
    for (let originX = 0; originX < tile; originX += 1) {
      const raw = scoresAt(field, originX, originY);
      let low = Infinity, high = -Infinity;
      for (const value of raw) { if (value < low) low = value; if (value > high) high = value; }
      if (high === low) continue;
      const threshold = (low + high) / 2;
      const lighter = raw.map((value) => value > threshold);
      const confidence = separation(raw, lighter);

      // The fallback's shortlist: the few origins whose fold splits into two levels most cleanly.
      if (cleanest.length < FALLBACK_ORIGINS || confidence > cleanest[cleanest.length - 1].separation) {
        cleanest.push({ x: originX, y: originY, threshold, separation: confidence });
        cleanest.sort((left, right) => right.separation - left.separation);
        if (cleanest.length > FALLBACK_ORIGINS) cleanest.pop();
      }

      for (const sign of [1, -1]) {
        const cells = sign > 0 ? lighter : raw.map((value) => value < threshold);
        const mark = decodeTile(cells, FOLD_ERRORS);
        if (!mark) continue;
        // Lines have only ever carried v2. Do not multiply the weaker legacy format's trials.
        if (field.horizontal && (mark.version !== 2 || !hasHorizontalCarrier(raw, sign, encodeTile(mark.digest, 2)))) continue;
        decoded[sign as 1 | -1] = true;
        const tiles = Math.floor((field.width - originX) / tile) * Math.floor((field.height - originY) / tile);
        const candidate: Candidate = { x: originX, y: originY, sign, threshold, mark, version: mark.version, errors: mark.errors, confidence, tiles };
        if (shortlist.length < GRADED_CANDIDATES || byRank(candidate, shortlist[shortlist.length - 1]) < 0) {
          shortlist.push(candidate);
          shortlist.sort(byRank);
          if (shortlist.length > GRADED_CANDIDATES) shortlist.pop();
        }
      }
    }
  }

  let likely: ReadResult | null = null;
  let candidate: ReadResult | null = null;
  for (const entry of shortlist) {
    const graded = grade(field, entry.x, entry.y, entry.sign, entry.threshold, entry.mark, entry.confidence);
    if (graded.grade === 'confirmed') return { read: graded, candidate: null };
    if (graded.grade === 'likely') likely ??= graded;
    else candidate ??= graded;
  }
  if (likely) return { read: likely, candidate: null };
  for (const sign of [1, -1]) {
    if (decoded[sign as 1 | -1]) continue;
    const alone = readTilesAlone(field, sign, cleanest);
    if (alone) return { read: alone, candidate: null };
  }
  return { read: null, candidate };
}

/** Reads an image and returns the account digest written across it, or why it could not. */
export function readScreenMark(image: RasterImage, options: ReadOptions = {}): ReadOutcome {
  const { width, height } = image;
  const luma = luminance(image);
  const tried: { scale: number; tiles: number }[] = [];
  let candidate: ReadResult | null = null;
  let likely: ReadResult | null = null;

  for (const scale of options.scales ?? [1, 2, 3]) {
    const tile = TILE_PX * scale;
    // A whole tile has to be present whatever the crop's phase, which takes two tiles less a pixel.
    if (width < tile * 2 - 1 || height < tile * 2 - 1) {
      tried.push({ scale, tiles: 0 });
      continue;
    }
    tried.push({ scale, tiles: Math.floor(width / tile) * Math.floor(height / tile) });

    const field = prepare(luma, width, height, scale);
    if (!field) continue;
    const found = searchScale(field);
    if (found.read?.grade === 'confirmed') return { ok: true, ...found.read };
    if (found.read) { likely = found.read; break; }
    if (found.candidate && outranks(found.candidate, candidate)) candidate = found.candidate;
  }

  // Preserve the dot reader (including v1 screenshots) as the first route at every scale. A
  // horizontal response gets exactly the same decoder, error budgets and corroboration rules;
  // changing the appearance never grants a less certain read or requires the operator to choose.
  for (const maskContent of [true, false]) for (const scale of options.scales ?? [1, 2, 3]) {
    const tile = TILE_PX * scale;
    if (width < tile * 2 - 1 || height < tile * 2 - 1) continue;
    const field = prepare(luma, width, height, scale, true, maskContent);
    if (!field) continue;
    const found = searchScale(field);
    // A second filter may strengthen evidence for the first digest, never replace it with a
    // different identity. A conflict leaves the original read and its original grade intact.
    if (found.read && (!likely || (found.read.version === likely.version && found.read.digest === likely.digest))) {
      if (found.read.grade === 'confirmed') return { ok: true, ...found.read };
      if (!likely || outranks(found.read, likely)) likely = found.read;
    }
    if (found.candidate && outranks(found.candidate, candidate)) candidate = found.candidate;
  }
  if (likely) return { ok: true, ...likely };

  let reason: string;
  if (tried.every((entry) => entry.tiles === 0)) {
    reason = `That clipping is too small. A whole tile needs ${TILE_PX * 2 - 1}px each way at 1x, or ${TILE_PX * 4 - 1}px from a retina screen.`;
  } else if (candidate) {
    reason = candidate.errors
      ? `Not a read: the fold decoded with ${candidate.errors} corrected cell${candidate.errors === 1 ? '' : 's'} and nothing corroborated it. Get a second sample.`
      : 'Not a read: the fold decoded, but nothing in the image corroborated it. Get a second sample.';
  } else {
    reason = 'No screen mark was found. The image may have been resized, re-encoded hard, or taken from somewhere without the mark.';
  }
  return { ok: false, tried, reason, ...(candidate ? { candidate } : {}) };
}

// ── Recovering a screenshot that has been through something ─────────────────

/** Bilinear resample. A screenshot that was resized no longer has the tile on a whole-pixel pitch. */
export function resample(image: RasterImage, factor: number): RasterImage {
  const width = Math.max(1, Math.round(image.width * factor));
  const height = Math.max(1, Math.round(image.height * factor));
  const out = new Uint8ClampedArray(width * height * 4);
  const stepX = image.width / width;
  const stepY = image.height / height;

  for (let y = 0; y < height; y += 1) {
    const sourceY = Math.min(image.height - 1, (y + 0.5) * stepY - 0.5);
    const y0 = Math.max(0, Math.floor(sourceY));
    const y1 = Math.min(image.height - 1, y0 + 1);
    const fy = sourceY - y0;
    for (let x = 0; x < width; x += 1) {
      const sourceX = Math.min(image.width - 1, (x + 0.5) * stepX - 0.5);
      const x0 = Math.max(0, Math.floor(sourceX));
      const x1 = Math.min(image.width - 1, x0 + 1);
      const fx = sourceX - x0;
      const target = (y * width + x) * 4;
      for (let channel = 0; channel < 4; channel += 1) {
        const topLeft = image.data[(y0 * image.width + x0) * 4 + channel];
        const topRight = image.data[(y0 * image.width + x1) * 4 + channel];
        const bottomLeft = image.data[(y1 * image.width + x0) * 4 + channel];
        const bottomRight = image.data[(y1 * image.width + x1) * 4 + channel];
        const top = topLeft + (topRight - topLeft) * fx;
        const bottom = bottomLeft + (bottomRight - bottomLeft) * fx;
        out[target + channel] = top + (bottom - top) * fy;
      }
    }
  }
  return { data: out, width, height };
}

/**
 * Finds the flattest window in an image and returns it.
 *
 * A screenshot of a busy page is mostly content, and content is what the read has to see past. Where
 * there is room to choose, a window of empty card beats the same area of headings and buttons —
 * often the difference between a marginal image and a clean answer.
 */
export function quietestRegion(image: RasterImage, size: number): RasterImage {
  if (image.width <= size || image.height <= size) return image;
  const luma = luminance(image);
  const table = integral(luma, image.width, image.height);
  const squares = new Float32Array(luma.length);
  for (let index = 0; index < luma.length; index += 1) squares[index] = luma[index] * luma[index];
  const squareTable = integral(squares, image.width, image.height);
  const stride = image.width + 1;
  const area = size * size;

  let bestX = 0, bestY = 0, bestVariance = Infinity;
  const step = Math.max(8, Math.floor(size / 8));
  for (let y = 0; y + size <= image.height; y += step) {
    for (let x = 0; x + size <= image.width; x += step) {
      const total = boxSum(table, stride, x, y, x + size, y + size);
      const totalSquares = boxSum(squareTable, stride, x, y, x + size, y + size);
      const variance = totalSquares / area - (total / area) ** 2;
      if (variance < bestVariance) { bestVariance = variance; bestX = x; bestY = y; }
    }
  }

  const out = new Uint8ClampedArray(size * size * 4);
  for (let y = 0; y < size; y += 1) {
    const from = ((bestY + y) * image.width + bestX) * 4;
    out.set(image.data.subarray(from, from + size * 4), y * size * 4);
  }
  return { data: out, width: size, height: size };
}

/** Lags the pitch is looked for at, in pixels: a tile from a third of its size to just over three times it. */
const PITCH_LAGS = { min: 24, max: 208 };
/**
 * The mask for the pitch estimate, tighter than the fold's. The fold keeps every sample the mark
 * could have produced through JPEG ringing and paint edges, which is what thirty was measured for.
 * The autocorrelation needs the mark to outweigh the page instead: the mark answers at about four,
 * text edges answer between ten and thirty and repeat at their own pitch, and with them out the
 * true pitch is among the two strongest peaks on four real clips in five rather than one in three.
 */
const PITCH_MASK = 10;

/**
 * The periods an image repeats at, strongest first, from the autocorrelation of its response maps.
 *
 * A resized screenshot still repeats — at some pitch other than sixty-four pixels — and the
 * response map still answers to it. Correlating that map with itself along each axis puts a peak
 * at the pitch, and the peak's lag says what the image was scaled by, which is a far better use of
 * a resample than guessing. Two maps are correlated because the cell size decides what a map can
 * see: the native one answers to a mark that was shrunk, and the retina one to a mark that was
 * enlarged until its dots are wider than a native window, where the native map goes flat.
 *
 * The maps are masked harder than the fold masks them, because page content is what makes this
 * hard: text rows repeat too, at their own pitch and far louder than the mark, and the mark's
 * period only shows once the loud samples are out. Each lag is a mean over the products that fit
 * at it, normalised by the mean square, so a long lag is not penalised for having fewer terms.
 * Page content still has periods of its own, which is why more than one peak comes back — and why
 * what comes back is a shortlist for the deep read to try, not an answer.
 */
export function estimatePitch(image: RasterImage, lags: { min: number; max: number } = PITCH_LAGS): { lag: number; strength: number }[] {
  const luma = luminance(image);
  const peaks = new Map<number, number>();

  for (const cell of [CELL_PX, CELL_PX * 2]) {
    const { map, mapWidth, mapHeight } = responseMap(luma, image.width, image.height, cell);
    let energy = 0;
    for (let index = 0; index < map.length; index += 1) {
      if (map[index] > PITCH_MASK || map[index] < -PITCH_MASK) map[index] = 0;
      energy += map[index] * map[index];
    }
    if (!energy) continue;
    const meanSquare = energy / map.length;

    const alongX = (lag: number): number => {
      let total = 0;
      for (let y = 0; y < mapHeight; y += 1) {
        const row = y * mapWidth;
        for (let x = 0; x + lag < mapWidth; x += 1) total += map[row + x] * map[row + x + lag];
      }
      return total / ((mapWidth - lag) * mapHeight) / meanSquare;
    };
    const alongY = (lag: number): number => {
      let total = 0;
      for (let y = 0; y + lag < mapHeight; y += 1) {
        const row = y * mapWidth, below = (y + lag) * mapWidth;
        for (let x = 0; x < mapWidth; x += 1) total += map[row + x] * map[below + x];
      }
      return total / (mapWidth * (mapHeight - lag)) / meanSquare;
    };

    for (const [length, correlate] of [[mapWidth, alongX], [mapHeight, alongY]] as const) {
      const last = Math.min(lags.max + 1, length - 1);
      if (last < lags.min + 1) continue;
      const curve = new Float64Array(last + 1);
      for (let lag = lags.min - 1; lag <= last; lag += 1) curve[lag] = correlate(lag);
      for (let lag = lags.min; lag < last; lag += 1) {
        if (curve[lag] > 0 && curve[lag] > curve[lag - 1] && curve[lag] >= curve[lag + 1]) {
          peaks.set(lag, Math.max(peaks.get(lag) ?? -Infinity, curve[lag]));
        }
      }
    }
  }
  return [...peaks.entries()].map(([lag, strength]) => ({ lag, strength })).sort((left, right) => right.strength - left.strength);
}

export interface DeepReadOptions extends ReadOptions {
  /** Resampling factors to try after the pitch-guided ones, in place of the twelve fixed ones. */
  factors?: number[];
  /** Also try the quietest window of this size. Off when zero. */
  quietWindow?: number;
  /**
   * After everything else, sweep a fine grid of factors for a screenshot scaled by an amount the
   * fixed list steps over. Slow — dozens of resamples — so only for an image that matters.
   */
  exhaustive?: boolean;
  /** Called with each attempt, so a tool can say what it is doing. */
  onAttempt?: (label: string) => void;
}

export interface DeepReadOutcome {
  outcome: ReadOutcome;
  /** What produced the answer, for a tool to report: 'as supplied', 'rescaled ×0.75', and so on. */
  via: string;
  attempts: string[];
  /** Which attempt produced the unconfirmed candidate on a failed outcome, when there was one. */
  candidateVia?: string;
}

/** The common resizes, most common first: the last resort once the pitch guidance has had its turn. */
const FIXED_FACTORS = [2, 1.5, 1.25, 0.75, 0.6667, 0.5, 3, 2.5, 1.75, 1.3333, 0.8, 0.4];
/** A factor within this of one already tried is the same attempt again. */
const FACTOR_TOLERANCE = 0.02;
/** Rescales any route is allowed to ask for. */
const FACTOR_RANGE = { min: 0.3, max: 3.5 };
/** The exhaustive sweep's step, as a ratio between neighbouring factors: fine enough that no pitch falls between two of them. */
const SWEEP_STEP = 1.04;

const factorLabel = (factor: number): string => `rescaled ×${factor.toFixed(4).replace(/\.?0+$/, '')}`;

/**
 * Reads a screenshot that may have been through a resize, a chat client, or both.
 *
 * The plain read assumes the picture still has the tile on a whole-pixel pitch, which is true of a
 * screenshot pasted as taken and false of one that has been scaled to fit a message window. So when
 * that fails, the pitch the image actually repeats at is estimated and the image resampled to put
 * it back on sixty-four pixels (or a hundred and twenty-eight, for a retina capture); the quietest
 * window is tried on its own in case page content is what is drowning the mark; and then the
 * common resizes are tried one by one. That last sweep is not behind a flag, although the design
 * wanted it to be: measured on real clips, the pitch estimate puts the true pitch among its two
 * strongest peaks about half the time on a shrunk screenshot and almost never on an enlarged
 * retina one, where page content repeats more loudly than the mark, and a screenshot the old
 * reader recovered has to stay recoverable. The estimate is kept because when it is right it
 * saves the sweep. `exhaustive` goes further, to a fine grid for a factor the fixed list steps over.
 *
 * Every attempt still has to clear the same validation and evidence rules. More attempts add
 * hypotheses, so false-positive checks must cover this entire recovery path. Once an attempt
 * reads an identity, later attempts can only strengthen that same identity's grade. The best
 * unconfirmed candidate rides on a failure so a second sample can be checked against it.
 */
export function readScreenMarkDeep(image: RasterImage, options: DeepReadOptions = {}): DeepReadOutcome {
  const attempts: string[] = [];
  let candidate: ReadResult | null = null;
  let candidateVia: string | undefined;
  const saved: { outcome: ({ ok: true } & ReadResult) | null; via: string } = { outcome: null, via: '' };
  const attempt = (label: string, run: () => ReadOutcome): ReadOutcome => {
    attempts.push(label);
    options.onAttempt?.(label);
    const outcome = run();
    // A likely read can arrive before a better resampling of the same image. Keep its identity
    // and continue for independent spatial corroboration; another account never replaces it.
    if (outcome.ok && (!saved.outcome || (outcome.digest === saved.outcome.digest && outcome.version === saved.outcome.version && outcome.grade === 'confirmed'))) {
      saved.outcome = outcome;
      saved.via = label;
    }
    if (!outcome.ok && outcome.candidate && outranks(outcome.candidate, candidate)) {
      candidate = outcome.candidate;
      candidateVia = label;
    }
    return outcome;
  };

  const plain = attempt('as supplied', () => readScreenMark(image, options));
  if (plain.ok && plain.grade === 'confirmed') return { outcome: plain, via: 'as supplied', attempts };

  const tried: number[] = [];
  const rescaled = (factor: number, label: string): ReadOutcome | null => {
    if (tried.some((done) => Math.abs(done - factor) <= FACTOR_TOLERANCE * factor)) return null;
    const scaled = resample(image, factor);
    if (scaled.width < TILE_PX * 2 || scaled.height < TILE_PX * 2) return null;
    tried.push(factor);
    return attempt(label, () => readScreenMark(scaled, options));
  };

  // A screenshot scaled to fit somewhere is the common damage, so undo it rather than give up. The
  // pitch found could be a tile at native size or at retina size, so both are tried.
  for (const { lag } of estimatePitch(image).slice(0, 2)) {
    for (const pitch of [TILE_PX, TILE_PX * 2]) {
      const factor = pitch / lag;
      if (factor < FACTOR_RANGE.min || factor > FACTOR_RANGE.max || Math.abs(factor - 1) < FACTOR_TOLERANCE) continue;
      rescaled(factor, `${factorLabel(factor)} (pitch ${lag}px)`);
      if (saved.outcome?.grade === 'confirmed') return { outcome: saved.outcome, via: saved.via, attempts };
    }
  }

  const window = options.quietWindow ?? 512;
  if (window > 0 && (image.width > window || image.height > window)) {
    const quiet = quietestRegion(image, window);
    attempt(`quietest ${window}px window`, () => readScreenMark(quiet, options));
    if (saved.outcome?.grade === 'confirmed') return { outcome: saved.outcome, via: saved.via, attempts };
  }

  for (const factor of options.factors ?? FIXED_FACTORS) {
    rescaled(factor, factorLabel(factor));
    if (saved.outcome?.grade === 'confirmed') return { outcome: saved.outcome, via: saved.via, attempts };
  }

  if (options.exhaustive) {
    for (let factor = FACTOR_RANGE.min; factor <= FACTOR_RANGE.max; factor *= SWEEP_STEP) {
      rescaled(Number(factor.toFixed(4)), `${factorLabel(factor)} (sweep)`);
      if (saved.outcome?.grade === 'confirmed') return { outcome: saved.outcome, via: saved.via, attempts };
    }
  }

  if (saved.outcome) return { outcome: saved.outcome, via: saved.via, attempts };

  return {
    outcome: !plain.ok && candidate ? { ...plain, candidate } : plain,
    via: 'nothing worked',
    attempts,
    ...(candidateVia ? { candidateVia } : {}),
  };
}

/**
 * Stretches the faint layer into something a person can look at.
 *
 * This does not help the decoder, which is already differential and linear — it is for confirming by
 * eye that a lattice is present in an image that would not read. An even grid of dots means a mark
 * is there; the absence of one means nothing either way, because scribbling over a screenshot
 * destroys the grid's visibility along with the read, and a tampered marked image measures the same
 * as one that was never marked.
 */
export function amplify(image: RasterImage, gain = 14): RasterImage {
  const { width, height } = image;
  const luma = luminance(image);
  const table = integral(luma, width, height);
  const stride = width + 1;
  const out = new Uint8ClampedArray(width * height * 4);
  const radius = 4;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const x0 = Math.max(0, x - radius), y0 = Math.max(0, y - radius);
      const x1 = Math.min(width, x + radius + 1), y1 = Math.min(height, y + radius + 1);
      const local = boxSum(table, stride, x0, y0, x1, y1) / ((x1 - x0) * (y1 - y0));
      const value = 128 + (luma[y * width + x] - local) * gain;
      const index = (y * width + x) * 4;
      out[index] = out[index + 1] = out[index + 2] = value;
      out[index + 3] = 255;
    }
  }
  return { data: out, width, height };
}
